import React from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import { EventSnapshot, firstElementByRole, ModerationEvent } from '../../models';
import { entityDetailUrl, LinkableEntityKind } from '../../helpers/entityDetailUrl';
import { subjectIdSegment } from '../../helpers/subjectId';
import TriggerBadge from '../../components/TriggerBadge/TriggerBadge';
import styles from './review.module.css';

export interface ReviewHeaderProps {
  event: ModerationEvent;
  snapshot: EventSnapshot;
  testId?: string;
}

// All admin-app links in the header open in a new tab so the reviewer keeps
// their place in the review flow. Plain <a> (not react-router <Link>) since
// the new tab does a full mount anyway.
function ExternalAdminLink({
  to,
  children,
  testId,
}: {
  to: string;
  children: React.ReactNode;
  testId?: string;
}): React.ReactElement {
  return (
    <a
      className={styles.headerLink}
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
    >
      {children}
    </a>
  );
}

export function ReviewHeader({ event, snapshot, testId }: ReviewHeaderProps): React.ReactElement {
  const title = snapshot.pageNameSnapshot
    ?? I18n.t(`${I18N_BASE}.queue.card.fallbackTitle`, {
      kind: I18n.t(`${I18N_BASE}.subjectKind.${event.subjectKind}`),
      id: event.contentModerationEventId,
    });

  const subject = subjectIdSegment(event);
  // subjectIdSegment returns the i18n key (with subject kind) + id; map the
  // subject kind back to the entity-detail-url kind so we can link the chip.
  const subjectLinkKind: LinkableEntityKind | null = (() => {
    switch (event.subjectKind) {
      case 'fundraiserPage':
        return event.fundraiserId != null ? 'fundraiser' : null;
      case 'teamPage':
        return event.teamId != null ? 'team' : null;
      case 'collectionPage':
        return event.collectionId != null ? 'collection' : null;
      default:
        return null;
    }
  })();

  const organizationName =
    firstElementByRole(snapshot.elements, 'organizationName')?.value ?? null;

  // Each chip is rendered separately so the IDs become clickable links. The
  // bullet separators stay as plain text between them.
  const chips: React.ReactNode[] = [];
  if (subject && subjectLinkKind) {
    chips.push(
      <ExternalAdminLink
        key="subject"
        to={entityDetailUrl(subjectLinkKind, subject.id)}
        testId="header-link-subject"
      >
        {I18n.t(subject.i18nKey, { id: subject.id })}
      </ExternalAdminLink>,
    );
  } else if (subject) {
    // Defensive — we have a label but no link kind (shouldn't happen given
    // the switch above, but keep the chip visible if it does).
    chips.push(
      <span key="subject">{I18n.t(subject.i18nKey, { id: subject.id })}</span>,
    );
  }
  if (organizationName != null && event.linkedOrganizationId != null) {
    chips.push(
      <ExternalAdminLink
        key="organization"
        to={entityDetailUrl('organization', event.linkedOrganizationId)}
        testId="header-link-organization"
      >
        {organizationName}
      </ExternalAdminLink>,
    );
  } else if (organizationName != null) {
    chips.push(<span key="organization">{organizationName}</span>);
  }
  if (event.linkedAccountId != null) {
    chips.push(
      <ExternalAdminLink
        key="account"
        to={entityDetailUrl('account', event.linkedAccountId)}
        testId="header-link-account"
      >
        {I18n.t(`${I18N_BASE}.detail.header.accountLabel`, {
          id: event.linkedAccountId,
        })}
      </ExternalAdminLink>,
    );
  }

  return (
    <header className={styles.header} data-testid={testId}>
      <div className={styles.titleGroup}>
        <h1 className={styles.title}>
          {title}
          {event.pageUrlFull ? (
            <>
              {' '}
              <a
                className={styles.headerExternalLink}
                href={event.pageUrlFull}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-link-pageUrlFull"
              >
                {I18n.t(`${I18N_BASE}.detail.header.viewLivePage`)}
              </a>
            </>
          ) : null}
        </h1>
        <span className={styles.subtitle}>
          {chips.map((chip, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 ? ' · ' : ''}
              {chip}
            </React.Fragment>
          ))}
        </span>
      </div>
      <TriggerBadge trigger={event.triggerReason} />
    </header>
  );
}

export default ReviewHeader;
